import React from "react";
import { connect } from "react-redux";
import { Col, Row, Form, Button, Overlay, Popover } from 'react-bootstrap';
import PropTypes from "prop-types";
import { ReactTags } from "react-tag-autocomplete";

import { withNavigation } from "../../../utils/wrapper";
import Table from "../../../layout/table";
import Hamburger from "../../../layout/kebab";
import PersonSearch from "../../../layout/select/person-search";
import { search, batch } from "../../../actions/matter.party";
import { get as getSettings } from "../../../actions/settings";
import { isGuid } from "../../../utils/string";

class Parties extends React.Component {

    constructor(props) {
        super(props);
        this.loadSeq = 0;
        this.state = {
            page: 0,
            size: 10,
            search: "",
            order: "lastName",
            index: 0,
            direction: "DESC",
            data: [],
            pages: 0,
            menu: {}
        };
    }

    getData = () => {
        return this.state.data;
    }

    componentDidMount = async () => {
        await this.loadData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.clientSeed?.person?.uuid !== this.props.clientSeed?.person?.uuid) {
            this.applyClientSeed();
        }
    }

    normalizeRoleArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;

        if (typeof val === "string") {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                // fallback if someone stored "A,B,C"
                return val.split(",").map(s => s.trim()).filter(Boolean);
            }
        }

        return [];
    };

    toTag = (x) => {
        if (x == null) return null;
        if (typeof x === "string") return { value: x, label: x };
        // if already an object, try to coerce
        const v = x.value ?? x.label ?? String(x);
        const l = x.label ?? x.value ?? String(x);
        return { value: v, label: l };
    };

    buildClientSeedRow = () => {
        const seed = this.props.clientSeed || {};
        const person = seed.person || {};
        if (!isGuid(person.uuid)) return null;

        const displayName = person.displayName || [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
        const [fallbackFirstName, ...fallbackLastName] = displayName.split(" ");

        return {
            ...person,
            uuid: person.uuid,
            firstName: person.firstName || fallbackFirstName || "",
            lastName: person.lastName || fallbackLastName.join(" ") || "",
            flags: person.flags ?? 4,
            roles: [this.toTag(seed.role || "Plaintiff")],
            attorney: 0,
            changed: true,
            "Person_CaseParty.uuid": null
        };
    };

    applyClientSeed = () => {
        const seedRow = this.buildClientSeedRow();
        if (!seedRow) return;

        this.setState((prev) => {
            const existingRows = prev.data || [];
            const alreadyPresent = existingRows.some((row) => row?.uuid === seedRow.uuid);
            if (alreadyPresent) return null;

            const [blank = {}, ...rest] = existingRows;
            return {
                data: [
                    blank?.uuid ? {} : blank,
                    seedRow,
                    ...(blank?.uuid ? existingRows : rest),
                ]
            };
        });
    };

    loadData = async () => {
        const seq = ++this.loadSeq;

        const [rolesSetting, __data] = await Promise.all([
            this.props.getSettings({ authentic: true, name: "CASE_ROLES" }),
            this.props.search({
                page: this.state.page,
                size: this.state.size,
                order: this.state.order,
                direction: this.state.direction,
                assignment: "case",
                uuid: this.props.owner,
                search: this.state.search
            })
        ]);

        if (seq !== this.loadSeq) return;

        // suggestions list (typeahead list)
        const roleSuggestions =
            this.normalizeRoleArray(rolesSetting?.value).map(r => ({ value: r, label: r }));

        // ensure blank selector row at top
        const itemsRaw = [{}, ...(__data?.items ?? [])];

        // normalize selected roles for each row so ReactTags can render them
        const items = itemsRaw.map((row) => {
            if (!row?.uuid) return row; // keep the blank top row as-is

            const rawRoles = row.roles ?? row["Person_CaseParty.roles"]; // DB field likely here
            const rolesArr = this.normalizeRoleArray(rawRoles);

            return {
                ...row,
                roles: rolesArr.map(this.toTag).filter(Boolean),
                attorney: row.attorney ?? row["Person_CaseParty.attorney"] ?? 0
            };
        });

        const seedRow = this.buildClientSeedRow();
        const seededItems = seedRow && !items.some((row) => row?.uuid === seedRow.uuid)
            ? [{}, seedRow, ...items.filter((row, index) => index !== 0 || row?.uuid)]
            : items;

        let __count = 0;
        if((seededItems?.length || 0) > 0) {
            __count = Math.ceil(Number(__data.count) / Number(this.state.size));
        }

        this.setState({
            data: seededItems,
            roles: roleSuggestions,
            pages: __count
        });
    };

    buildSavePayload = () => {
        const changed = (this.state.data || []).filter(r => !!r.changed && isGuid(r.uuid));

        return changed.map(r => {
            const normalized = this.normalizeRoleTags(r.roles ?? r["Person_CaseParty.roles"]);
            return {
                personUuid: r.uuid,
                casePartyUuid: r["Person_CaseParty.uuid"] || null,
                roles: normalized.map(role => role.value ?? role.label ?? String(role)),
                attorney: Number(r.attorney) ? 1 : 0,
                deleted: !!r.deleted
            };
        });
    };

    // This is what the parent will call via ref
    saveChanges = async () => {
        const items = this.buildSavePayload();
        if (!items.length) return { saved: 0, message: "No changes." };

        // optional: disable UI while saving
        this.setState({ saving: true, saveError: null });

        try {
            const result = await this.props.batch({
                caseUuid: this.props.owner,
                items
            });

            // clear flags locally after success
            this.setState(prev => ({
                saving: false,
                data: (prev.data || []).map(r => (r.changed ? { ...r, changed: false } : r))
            }));

            // optional: reload from server to normalize everything
            await this.loadData();

            return { saved: items.length, result };
        } catch (err) {
            this.setState({ saving: false, saveError: err });
            throw err;
        }
    };

    onPartySelect = (e) => {
        // Adjust this line if PersonSearch sends a different shape
        const person = e?.target?.item?.data;
        if (!person) return;

        this.setState(prev => {
            // Drop the current first row (the blank selector row)
            const [, ...rest] = prev.data;

            // Build a new row for the selected person
            const newRow = {
                ...person,            // person.uuid, firstName, lastName, etc.
                roles: [],            // start with no roles selected
                attorney: 0,
                changed: true,        // mark as changed for saveChanges()
                "Person_CaseParty.uuid": null // make sure backend sees this as new assignment
            };

            return {
                data: [
                    {},        // NEW blank row at the top for the next selection
                    newRow,    // freshly-added person
                    ...rest    // all the existing rows from before
                ]
            };
        });
    };

    onAttorneyChange = (value, uuid) => {
        this.setState(prev => ({
            data: prev.data.map(row => {
                if (row.uuid !== uuid) return row; // leave other rows alone

                return {
                    ...row,
                    attorney: value ? 1 : 0,
                    changed: true
                };
            }),
        }));
    }

    onRowDelete = (uuid) => {
        this.setState(prev => ({
            data: prev.data.map(row => {
                if (row.uuid !== uuid) return row;
                if (!row["Person_CaseParty.uuid"]) return { ...row, _removeFromUi: true }; // new unsaved row
                return { ...row, deleted: true, changed: true };
            }).filter(r => !r._removeFromUi)
        }));
    };

    onPartyFormat = (e, row) => {
        return (row.uuid) ? (<>
            <Button
                onClick={() => {
                    this.closeHamburger();
                    this.props.navigate(`/dashboard/people/${row.uuid}`);
                }}
                variant="link"
            >
                {row["lastName"]}, {row["firstName"]}
            </Button>
            {(row.flags & 4) > 0 && (
                <button
                    type="button"
                    className="badge rounded-pill bg-success"
                    style={{ marginLeft: 5 }}
                >
                    CLIENT
                </button>
            )}{(row.flags & 1) > 0 && (
                <button
                    type="button"
                    className="badge rounded-pill bg-success"
                    style={{ marginLeft: 5 }}
                >
                    JUDGE
                </button>
            )}{(row.flags & 2) > 0 && (
                <button
                    type="button"
                    className="badge rounded-pill bg-success"
                    style={{ marginLeft: 5 }}
                >
                    LEAD
                </button>
            )}{(row.flags & 8) > 0 && (
                <button
                    type="button"
                    className="badge rounded-pill bg-success"
                    style={{ marginLeft: 5 }}
                >
                    STAFF
                </button>
            )}
        </>) : <>
            <PersonSearch id="uuid" name="uuid" onChange={(e) => this.onPartySelect(e)} />
        </>;
    }

    onRoleAdd = (role, uuid) => {
        this.setState(prev => ({
            data: prev.data.map(row => {
                if (row.uuid !== uuid) return row;

                const existing = (row.roles || []).some(
                    r => (r.value ?? r.label) === (role.value ?? role.label)
                );
                if (existing) return row;

                return {
                    ...row,
                    roles: [...(row.roles || []), role],
                    changed: true
                };
            }),
        }));
    };

    onRoleDelete = (index, uuid) => {
        this.setState(prev => ({
            data: prev.data.map(row => {
                if (row.uuid !== uuid) return row;

                return {
                    ...row,
                    roles: (row.roles || []).filter((_, i) => i !== index),
                    changed: true
                };
            }),
        }));
    };

    onAttorneyFormat = (e, row) => {
        if (!isGuid(row?.uuid)) return null;
        return (
            <Form.Check
                type="switch"
                id={`attorney-${row.uuid}`}
                label="attorney"
                checked={Number(row.attorney) === 1}
                onChange={(e) => this.onAttorneyChange(e.target.checked, row.uuid)}
            />
        );
    };

    normalizeRoleTags = (val) => {
        const arr = this.normalizeRoleArray(val);        // always returns an array
        return arr.map(this.toTag).filter(Boolean);      // always -> [{label,value}]
    };

    onRolesFormat = (e, row) => {
        if (!isGuid(row?.uuid)) return null;
        const selected = this.normalizeRoleTags(row?.roles ?? row?.["Person_CaseParty.roles"]);
        const suggestions = Array.isArray(this.state.roles) ? this.state.roles : [];

        return (
            <ReactTags
                selected={selected}
                suggestions={suggestions}
                onDelete={(tagIndex) => this.onRoleDelete(tagIndex, row.uuid)}
                onAdd={(tag) => this.onRoleAdd(tag, row.uuid)}
                placeholderText="Start typing"
                activateFirstOption
                collapseOnSelect
                delimiterKeys={["Enter", "Tab", ","]}
                newOptionText="%value%"
            />
        );
    };

    openHamburger = (event, uuid) => {
        event.preventDefault();
        this.setState({
            menu: {
                show: true,
                uuid,
                target: event.currentTarget
            }
        });
    }

    closeHamburger = () => {
        this.setState(prev => ({
            menu: {
                ...prev.menu,
                show: false,
                target: null
            }
        }));
    }

    onActionFormat = (e, row) => {
        if (!isGuid(row.uuid)) return <></>;

        return (<>
            <Hamburger variant="link" vertical={false} onClick={(e) => this.openHamburger(e, row.uuid)} />
        </>)
    }

    onChange = (data) => {
        if (this.props.onChange) this.props.onChange(data);
    };

    onPageChange = (e) => {
        this.setState({
            page: Number(e)
        }, this.loadData);
    }

    onColumnClick = (e) => {
        const __array = this.headers;
        const __index = __array.findIndex((item) => {
            return item.header === e.header && item.source === e.source;
        });

        this.setState({
            index: __index,
            direction: this.state.index === __index && this.state.direction === "DESC" ? "ASC" : "DESC",
            order: __array[__index].source,
            page: 0
        }, this.loadData);
    }

    onSearch = (e) => {
        this.setState({
            search: e
        }, this.loadData);
    }

    onSize = (e) => {
        this.setState({
            page: 0,
            size: Number(e)
        }, this.loadData);
    }

    get headers() {
        return [
            {
                header: "Party Name",
                sortable: true,
                source: "lastName",
                className: "text-muted text-small text-uppercase w-40",
                onRender: this.onPartyFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-10",
                onRender: this.onAttorneyFormat
            },
            {
                header: "Roles",
                sortable: false,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-50",
                onRender: this.onRolesFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                onRender: this.onActionFormat
            }
        ]
    }



    render() {
        return (
            <>
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Parties</h2>
                    </Col>
                    <Col xs="12">
                        <Table
                            headers={this.headers}
                            page={this.state.data}
                            sort={{ index: this.state.index, direction: this.state.direction }}
                            size={this.state.size}
                            index={this.state.page}
                            pages={this.state.pages}
                            onColumnClick={this.onColumnClick}
                            onPageChange={this.onPageChange}
                            onSearch={this.onSearch}
                            onSize={this.onSize}
                            searchable
                            sizable
                        />
                    </Col>
                </Row>
                
                <Overlay
                    target={this.state.menu.target}
                    show={this.state.menu.show}
                    placement="bottom-end"
                    rootClose
                    onHide={this.closeHamburger}
                >
                    {(props) => (
                        <Popover {...props}>
                                           
                            <Popover.Body>
                                <Row className="mb-2">
                                    <Col className="d-flex flex-row mt-1">
                                               
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => {
                                                this.closeHamburger();
                                                this.props.navigate(`/dashboard/people/${this.state.menu.uuid}`);
                                            }}
                                        >
                                            Open
                                        </button></Col></Row><Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => {
                                                this.closeHamburger();
                                                this.onRowDelete(this.state.menu.uuid)
                                            }}
                                        >
                                            Delete
                                        </button></Col>
                                </Row>
                            </Popover.Body>
                        </Popover>
                    )}
                </Overlay>
            </>
        )
    }
}

Parties.propTypes = {
    auth: PropTypes.object.isRequired
}

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    })
}


export default withNavigation(
    connect(
        mapStateToProps,
        {
            search,
            batch,
            getSettings
        },
        null,
        { forwardRef: true }
    )(Parties));
